import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, CalendarClock, Pencil, ChevronRight, FileText, AlertTriangle, Check, Wrench, User, Box, MapPin } from "lucide-react";
import EmptyState from "../components/EmptyState";
import { Button, Spinner } from "../components/ui/kit";
import NovyZapisDialog from "../components/NovyZapisDialog";
import NovaAktivita from "../components/NovaAktivita";
import ZarizeniForm from "../components/ZarizeniForm";
import { fmtYmd, zapisChip, typZapisuLabel, fmtHodiny, ZAVAZNOST_LABEL } from "../lib/servis";
import { fmtKc } from "../lib/format";
import api, { souborUrl } from "../lib/api";
import { Drobecky, useNazevStranky, useUrlStav } from "../lib/navigace";
import { useAuth } from "../context/AuthContext";
import { jeSubdodavatel, subObchod, canEditAktivita } from "../lib/nav";
import Segment from "../components/Segment";
import DetailHlava, { KartaZalozky, KartaSekce, Pridat, StitekTmavy } from "../components/DetailHlava";
import RadekAktivity from "../components/RadekAktivity";
import { SkupinyAktivit } from "../lib/aktivity";

function ZapisRow({ z }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const chip = zapisChip(z);

  const toggle = async () => {
    const nx = !open; setOpen(nx);
    if (nx && !detail) {
      const { data } = await api.get(`/api/zapisy/${z.id}`);
      setDetail(data);
    }
  };
  const boduOk = (z.body || []).filter((b) => b.ok === true).length;
  const boduCelkem = (z.body || []).length;

  return (
    <div className="border-b border-line-softer last:border-0">
      <div className="flex items-center gap-3 md:gap-3.5 px-4 md:px-5 py-3 hover:bg-inputbg">
        <span className="h-9 w-9 rounded-[12px] bg-inputbg text-sec inline-flex items-center justify-center shrink-0"><Wrench size={16} strokeWidth={1.9} /></span>
        <button onClick={toggle} data-testid={`zapis-row-${z.id}`}
          className="min-w-0 flex-1 text-left">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-[13.5px] font-700 text-ink">{z.nazev || typZapisuLabel(z.typ_zapisu)}</span>
            <span className={`label-up px-2 py-0.5 rounded-chip ${chip.cls}`}>{chip.label}</span>
          </span>
          <span className="block text-[11.5px] text-muted mt-1">
            {boduCelkem ? `${boduOk} / ${boduCelkem} bodů` : "bez bodů"}
            {z.fotky?.length ? ` · ${z.fotky.length} fotek` : ""}
            {z.bez_zavad ? " · bez závad" : ""}
          </span>
        </button>
        <span className="amount text-[11.5px] text-muted shrink-0">{fmtYmd(z.datum)}</span>
        {/* Otevřít musí být vidět pořád — přes rozbalení náhledu uživatel
            netuší, že se dá zápis otevřít celý. */}
        <button onClick={() => nav(`/servis/zapisy/${z.id}`)} data-testid={`zapis-open-${z.id}`}
          className="shrink-0 text-[11.5px] font-700 text-accent hover:underline">Otevřít</button>
        <button onClick={toggle} aria-label={open ? "Sbalit náhled" : "Rozbalit náhled"} className="shrink-0">
          <ChevronRight size={16} className={`text-muted transition-transform ${open ? "rotate-90" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="px-4 md:px-5 pb-4 -mt-1" data-testid={`zapis-detail-${z.id}`}>
          {detail === null ? <div className="py-3"><Spinner /></div> : (
            <div className="rounded-card bg-inputbg/60 p-4 space-y-3 text-[12.5px]">
              {detail.zapis && <p className="text-sec leading-relaxed whitespace-pre-line">{detail.zapis}</p>}
              {(detail.nedostatky || []).map((n) => (
                <div key={n.id} className="flex items-start gap-2 text-danger">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{n.popis}</span>
                </div>
              ))}
              {(detail.fotky_urls || []).length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {detail.fotky_urls.map((f) => (
                    <img key={f.file_id} src={souborUrl(f.url)} alt="" className="h-16 w-16 rounded-icon object-cover border border-line-strong" />
                  ))}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button onClick={() => nav(`/servis/zapisy/${z.id}`)} className="text-[12px] font-700 text-accent">Otevřít zápis →</button>
                {detail.pdf_url && <a href={souborUrl(detail.pdf_url)} target="_blank" rel="noreferrer" className="text-[12px] font-700 text-sec flex items-center gap-1"><FileText size={13} /> PDF</a>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ZarizeniDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [z, setZ] = useState(undefined);
  const [novy, setNovy] = useState(false);
  // Nová aktivita ({typ, nazev, datum}) nebo úprava existující — formulář z kalendáře.
  const [novaAkt, setNovaAkt] = useState(null);
  const [editAkt, setEditAkt] = useState(null);
  const [zalozka, setZalozka] = useUrlStav("zalozka", "aktivity");
  const [edit, setEdit] = useState(false);
  const [tab, setTab] = useUrlStav("zapisy", "VSE");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/zarizeni/${id}`);
      setZ(data);
    } catch { setZ(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useNazevStranky(z?.nazev);

  const vyresit = async (nid) => {
    await api.patch(`/api/nedostatky/${nid}`, { stav: "VYRESENY" });
    load();
  };

  if (z === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (z === null) return <div className="card p-8"><EmptyState icon="PackageX" title="Zařízení nenalezeno"
    action={<Button variant="ghost" onClick={() => nav("/servis")}>Zpět na servis</Button>} /></div>;

  const clientName = z.client?.nazev_firmy || [z.client?.jmeno, z.client?.prijmeni].filter(Boolean).join(" ") || "—";
  const adr = z.adresa_instalace || {};
  // Interval 0 = bez pravidelného servisu, ale ručně naplánovaná prohlídka platí i tak.
  const bezServisu = z.servisni_interval_mesice === 0 && !z.planovana_prohlidka;
  const zarukaSkoncila = !!z.zaruka_do && z.zaruka_do < new Date().toISOString().slice(0, 10);
  let zapisy = z.zapisy || [];
  if (tab === "PROHLIDKY") zapisy = zapisy.filter((x) => x.typ_zapisu === "PROHLIDKA");
  if (tab === "OPRAVY") zapisy = zapisy.filter((x) => x.typ_zapisu === "OPRAVA" || x.typ_zapisu === "ZARUCNI_OPRAVA");
  const params = z.parametry || [];
  const aktivity = z.aktivity || [];
  const planovane = aktivity.filter((a) => a.stav !== "HOTOVA")
    .sort((a, b) => String(a.date_start).localeCompare(String(b.date_start)));
  // Už naplánovaná prohlídka se otevře k úpravě — druhá by se jen zdvojila.
  const naplanovatProhlidku = () => {
    const existujici = z.planovana_prohlidka && aktivity.find((a) => a.id === z.planovana_prohlidka.id);
    if (existujici) return setEditAkt(existujici);
    const dnes = new Date().toISOString().slice(0, 10);
    setNovaAkt({ typ: "Servisní prohlídka", datum: z.dalsi_prohlidka && z.dalsi_prohlidka >= dnes ? z.dalsi_prohlidka : null });
  };
  const otevrene = (z.nedostatky || []).filter((n) => n.stav === "OTEVRENY");
  const poTerminu = otevrene.filter((n) => n.po_terminu).length;

  const toggleAkt = async (a) => { await api.post(`/api/aktivity/${a.id}/hotovo`); load(); };
  const prohlidkaPod = bezServisu ? null
    : z.planovana_prohlidka ? `v kalendáři od ${z.planovana_prohlidka.cas}`
    : z.dalsi_prohlidka ? "podle intervalu, nenaplánováno" : null;

  return (
    <div data-testid="zarizeni-detail" className="flex flex-col gap-4">
      <Drobecky aktualni={z.nazev}
        cesta={[{ nazev: "Klienti", to: "/klienti" }, ...(z.client_id ? [{ nazev: clientName, to: `/klienti/${z.client_id}` }] : [])]} />

      <DetailHlava testid="zarizeni-hlava" druh={z.vyrobce ? `Zařízení · ${z.vyrobce}` : "Zařízení"} nazev={z.nazev}
        stitky={z.otevrenych_nedostatku > 0 && (
          <StitekTmavy barva="#f0b551">{z.otevrenych_nedostatku} {z.otevrenych_nedostatku === 1 ? "nedostatek" : "nedostatky"}</StitekTmavy>
        )}
        meta={[
          z.client_id && (
            <button onClick={() => nav(`/klienti/${z.client_id}`)} className="inline-flex items-center gap-1.5 font-600 text-white hover:text-white/80"
              data-testid="zarizeni-klient"><User size={13} />{clientName}</button>
          ),
          z.popis && <span className="inline-flex items-center gap-1.5"><Box size={13} />{z.popis}</span>,
          (adr.ulice || adr.mesto) && <span className="inline-flex items-center gap-1.5"><MapPin size={13} />{[adr.ulice, adr.mesto].filter(Boolean).join(", ")}</span>,
          z.technologie_id && (!jeSubdodavatel(user) || subObchod(user)) && (
            <button onClick={() => nav(`/technologie/${z.technologie_id}`)} data-testid="zarizeni-zakazka"
              className="inline-flex items-center gap-1 font-600 text-white/80 hover:text-white">
              <FileText size={13} /> Zakázka a nabídka <ChevronRight size={13} />
            </button>
          ),
        ].filter(Boolean)}
        vedlejsi={[{ text: "Naplánovat prohlídku", ikona: CalendarClock, onClick: naplanovatProhlidku, testid: "zarizeni-prohlidka" }]}
        hlavni={{ text: "Nový zápis", ikona: Plus, onClick: () => setNovy(true), testid: "zarizeni-novy-zapis" }}
        vice={[{ text: "Upravit zařízení", ikona: Pencil, onClick: () => setEdit(true), testid: "zarizeni-edit" }]}
        cta={[
          { text: "Zápis", ikona: Plus, onClick: () => setNovy(true), testid: "zarizeni-cta-zapis" },
          { text: "Naplánovat", ikona: CalendarClock, onClick: naplanovatProhlidku, testid: "zarizeni-cta-prohlidka" },
          { text: "Upravit", ikona: Pencil, onClick: () => setEdit(true), testid: "zarizeni-cta-upravit" },
        ]}
        /* Pořadí podle života zařízení: zprovoznění → další prohlídka → konec záruky. */
        dlazdice={[
          { popisek: "V provozu od", hodnota: fmtYmd(z.datum_zprovozneni), testid: "zarizeni-dlazdice-V provozu od" },
          { popisek: "Další prohlídka", hodnota: bezServisu ? "bez servisu" : fmtYmd(z.dalsi_prohlidka), testid: "zarizeni-dlazdice-Další prohlídka",
            zvyraznit: z.po_terminu ? "danger" : (!bezServisu && !z.planovana_prohlidka && z.dalsi_prohlidka ? "accent" : undefined), pod: prohlidkaPod },
          { popisek: "Záruka do", hodnota: fmtYmd(z.zaruka_do), pod: zarukaSkoncila ? "po záruce" : null, testid: "zarizeni-dlazdice-Záruka do" },
          { popisek: "Sériové číslo", hodnota: z.vyrobni_cislo || "—", testid: "zarizeni-dlazdice-Sériové číslo" },
        ]} />

      {z.rozpracovany_zapis && (
        <button onClick={() => nav(`/servis/zapisy/${z.rozpracovany_zapis.id}`)} data-testid="rozpracovany-zapis"
          className="w-full text-left rounded-card bg-[#fdf8ec] border border-[#ebd7a8] p-4 flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-[#c77a0a] shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-[13.5px] font-700 text-ink">Rozpracovaný zápis z {fmtYmd(z.rozpracovany_zapis.datum)}</span>
            <span className="block text-[11.5px] text-[#7a4c04] mt-0.5">{fmtHodiny(z.rozpracovany_zapis.hodiny_celkem)} · pokračovat a dokončit</span>
          </span>
          <span className="btn-primary h-9 px-4 shrink-0">Dokončit</span>
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        <div className="flex flex-col gap-4 min-w-0">
          {/* Otevřené nedostatky — termín odstranění je slib klientovi,
              takže patří na kartu, ne jen dovnitř zápisu. */}
          {otevrene.length > 0 && (
            <KartaSekce testid="zarizeni-nedostatky" titul="Otevřené nedostatky" pocet={otevrene.length}
              akce={poTerminu > 0 && (
                <span className="label-up px-2 py-0.5 rounded-chip bg-danger/10 text-danger">{poTerminu} po termínu</span>
              )}>
              <div className="border-t border-line">
                {otevrene.map((n) => (
                  <div key={n.id} className="px-4 md:px-5 py-3 border-b border-line-soft last:border-0 flex items-start gap-3"
                    data-testid={`nedostatek-${n.id}`}>
                    <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${n.po_terminu ? "bg-danger" : "bg-[#dc9a2c]"}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13.5px] font-600 text-ink">{n.popis}</span>
                      <span className="block text-[11.5px] text-muted mt-0.5">
                        {ZAVAZNOST_LABEL[n.zavaznost] || n.zavaznost}
                        {n.created_at ? ` · zjištěno ${fmtYmd(String(n.created_at).slice(0, 10))}` : ""}
                        {n.termin
                          ? <span className={n.po_terminu ? "text-danger font-700" : ""}>
                              {" · "}{n.po_terminu ? "termín prošel " : "do "}{fmtYmd(n.termin)}
                            </span>
                          : " · bez termínu"}
                      </span>
                    </span>
                    <button onClick={() => vyresit(n.id)} data-testid={`nedostatek-vyresit-${n.id}`}
                      className="btn-ghost h-8 px-3 shrink-0 text-[12px]"><Check size={14} /> Vyřešeno</button>
                  </div>
                ))}
              </div>
            </KartaSekce>
          )}

          {/* Aktivity a servisní zápisy pod záložkami — jako karta klienta. */}
          <KartaZalozky testid="zarizeni-aktivity" aktivni={zalozka} onZmena={setZalozka}
            zalozky={[
              { key: "aktivity", label: "Aktivity", pocet: aktivity.length, testid: "zarizeni-tab-aktivity" },
              { key: "zapisy", label: "Servisní zápisy", pocet: (z.zapisy || []).length, testid: "zarizeni-tab-zapisy" },
            ]}
            akce={zalozka === "aktivity"
              ? <Pridat text="Aktivita" onClick={() => setNovaAkt({})} testid="zarizeni-nova-aktivita" />
              : <Pridat text="Zápis" onClick={() => setNovy(true)} testid="zarizeni-pridat-zapis" />}>
            {zalozka === "aktivity" && (
              <div className="rise">
                {planovane.length === 0 && (
                  <>
                    <div className="label-up text-muted px-4 md:px-5 py-2 bg-[#fbfaf8] border-b border-line-soft">Naplánované · 0</div>
                    <div className="px-4 py-5 t-meta text-muted text-center border-b border-line-soft">Nic naplánováno. Prohlídku naplánuješ tlačítkem nahoře.</div>
                  </>
                )}
                {aktivity.length > 0 && (
                  <SkupinyAktivit items={aktivity} render={(a) => (
                    <RadekAktivity key={a.id} a={a} onOpen={() => setEditAkt(a)}
                      onToggle={canEditAktivita(user, a.oblast) ? () => toggleAkt(a) : null} />
                  )} />
                )}
              </div>
            )}
            {zalozka === "zapisy" && (
              <div className="rise">
                <div className="px-4 md:px-5 py-3 border-b border-line-soft">
                  <Segment className="inline-flex gap-1 p-1 bg-inputbg rounded-pill">
                    {[["VSE", "Vše"], ["PROHLIDKY", "Prohlídky"], ["OPRAVY", "Opravy"]].map(([v, l]) => (
                      <button data-on={tab === v} key={v} onClick={() => setTab(v)} data-testid={`zarizeni-tab-${v.toLowerCase()}`}
                        className={`tap md:min-h-0 px-3.5 h-9 md:h-8 rounded-pill t-meta font-700 ${tab === v ? "text-white" : "text-sec"}`}>{l}</button>
                    ))}
                  </Segment>
                </div>
                {zapisy.length === 0 ? (
                  <EmptyState icon="ClipboardList" title="Žádné zápisy" description="Založ první zápis z terénu." />
                ) : zapisy.map((zp) => <ZapisRow key={zp.id} z={zp} />)}
              </div>
            )}
          </KartaZalozky>
        </div>

        <div className="flex flex-col gap-4 min-w-0">
          {params.length > 0 && (
            <KartaSekce titul="Provozní parametry" telo>
              {params.map((p, i) => (
                <div key={i} className="flex justify-between gap-3 py-2 border-b border-line-soft last:border-0 t-meta md:text-[13px]">
                  <span className="text-sec">{p.nazev}</span><span className="amount font-700 text-ink">{p.hodnota}</span>
                </div>
              ))}
            </KartaSekce>
          )}
          <KartaSekce titul="Přehled" telo testid="zarizeni-prehled">
            <div className="flex justify-between py-2 border-b border-line-soft t-meta md:text-[13px]"><span className="text-sec">Odpracováno celkem</span><span className="amount font-700 text-ink">{fmtHodiny(z.souhrn?.hodiny_celkem)}</span></div>
            {z.souhrn?.servis_celkem_kc != null && (
              <div className="flex justify-between py-2 border-b border-line-soft t-meta md:text-[13px]"><span className="text-sec">Servis celkem</span><span className="amount font-700 text-ink">{fmtKc(z.souhrn.servis_celkem_kc)} Kč</span></div>
            )}
            <div className="flex justify-between py-2 t-meta md:text-[13px]"><span className="text-sec">Počet zápisů</span><span className="amount font-700 text-ink">{z.souhrn?.zapisu || 0}</span></div>
          </KartaSekce>
          {(z.dokumenty || []).length > 0 && (
            <KartaSekce titul="Dokumenty" pocet={z.dokumenty.length} telo>
              {z.dokumenty.map((d) => (
                <a key={d.id} href={souborUrl(d.soubor_url)} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 py-2 t-meta md:text-[13px] font-600 text-ink hover:text-accent">
                  <FileText size={15} className="text-muted" />{d.nazev || d.filename || "Dokument"}
                </a>
              ))}
            </KartaSekce>
          )}
        </div>
      </div>

      {novy && <NovyZapisDialog zarizeni={z} onClose={() => setNovy(false)} />}
      {novaAkt && (
        <NovaAktivita onClose={() => setNovaAkt(null)} onSaved={() => { setNovaAkt(null); load(); }}
          defaultClientId={z.client_id} zarizeniId={z.id} defaultOblast="SERVIS"
          defaultTyp={novaAkt.typ || null} defaultDate={novaAkt.datum || null}
          defaultNazev={novaAkt.typ ? `${novaAkt.typ} — ${z.nazev}` : null} />
      )}
      {editAkt && (
        <NovaAktivita aktivita={editAkt} onClose={() => setEditAkt(null)} onSaved={() => { setEditAkt(null); load(); }} />
      )}
      {edit && <ZarizeniForm zarizeni={z} onClose={() => setEdit(false)} onSaved={load}
        onDeleted={() => nav(z.client_id ? `/klienti/${z.client_id}` : "/servis")} />}
    </div>
  );
}
